import { useState, useEffect } from 'react';
import { Menu, X, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import AurocleLogo from './AurocleLogo';
import LanguageSwitcher from './LanguageSwitcher';
import { useLang } from '@/lib/translations';

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useLang();
  const n = t.nav;

  const NAV_ITEMS = [
    { label: n.overview, href: '#overview' },
    { label: n.capabilities, href: '#capabilities' },
    { label: n.architecture, href: '#architecture' },
    { label: n.industries, href: '#industries' },
    { label: n.security, href: '#security' },
    { label: n.pricing, href: '#pricing' },
    { label: n.insights, href: '/insights' },
    { label: n.contact, href: '#contact' },
  ];

  useEffect(() => {
    const handle = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handle);
    return () => window.removeEventListener('scroll', handle);
  }, []);

  return (
    <nav data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-aurocle-base/95 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16 lg:h-20">
        <a href="#overview" className="flex items-center gap-3" data-testid="nav-logo">
          <AurocleLogo size={36} />
          <span className="font-heading text-lg tracking-[0.15em] uppercase text-aurocle-text font-medium">
            Aurocle <span className="text-aurocle-gold">Agents</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-7">
          {NAV_ITEMS.map((item) => (
            item.href.startsWith('/') ? (
              <Link key={item.href} to={item.href}
                className="text-xs uppercase tracking-[0.12em] text-aurocle-platinum-muted hover:text-aurocle-gold transition-colors font-body"
                data-testid={`nav-link-${item.label.toLowerCase()}`}>{item.label}</Link>
            ) : (
              <a key={item.href} href={item.href}
                className="text-xs uppercase tracking-[0.12em] text-aurocle-platinum-muted hover:text-aurocle-gold transition-colors font-body"
                data-testid={`nav-link-${item.label.toLowerCase()}`}>{item.label}</a>
            )
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-2">
          <LanguageSwitcher />
          <Link to="/admin" className="p-2.5 border border-white/5 hover:border-aurocle-gold/30 transition-all group" title="Admin" data-testid="nav-admin-btn">
            <Shield size={14} className="text-aurocle-text-secondary group-hover:text-aurocle-gold transition-colors" />
          </Link>
          <a href="#contact" className="btn-outline text-xs px-4 py-2" data-testid="nav-book-meeting-btn">{n.bookMeeting}</a>
          <a href="#contact" className="btn-gold cta-burning text-xs px-4 py-2" data-testid="nav-request-demo-btn">{n.requestDemo}</a>
        </div>

        <div className="flex lg:hidden items-center gap-2">
          <LanguageSwitcher />
          <button onClick={() => setMobileOpen(!mobileOpen)} className="text-aurocle-text p-2" data-testid="nav-mobile-toggle">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-aurocle-base/98 backdrop-blur-xl border-t border-white/5 px-6 py-6 space-y-4">
          {NAV_ITEMS.map((item) => (
            item.href.startsWith('/') ? (
              <Link key={item.href} to={item.href} onClick={() => setMobileOpen(false)}
                className="block text-sm uppercase tracking-[0.1em] text-aurocle-text-secondary hover:text-aurocle-gold transition-colors py-2">{item.label}</Link>
            ) : (
              <a key={item.href} href={item.href} onClick={() => setMobileOpen(false)}
                className="block text-sm uppercase tracking-[0.1em] text-aurocle-text-secondary hover:text-aurocle-gold transition-colors py-2">{item.label}</a>
            )
          ))}
          <div className="pt-4 flex flex-col gap-3">
            <a href="#contact" onClick={() => setMobileOpen(false)} className="btn-gold cta-burning text-xs text-center">{n.requestDemo}</a>
            <Link to="/admin" onClick={() => setMobileOpen(false)} className="btn-outline text-xs text-center flex items-center justify-center gap-2">
              <Shield size={12} /> Admin
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
